/**
 * ConfigSection — 설정 속성 카탈로그(Config Reference) 섹션 (queue #665 ·
 * docs/biz-specs/common-showcase.md §UAT-2026-07-09-R665 §R665-3/§R665-4)
 *
 * `ModuleDetailBody` 에 삽입되는 노드 상세 공유 섹션. 데이터는 빌드타임 생성물
 * `data/config-properties.json`(apps/web/scripts/extract-config-metadata.mjs) 을
 * `useConfigProperties(node.artifact)` 로 조회한다 — BE 호출 없음(정적 import, 로딩 상태 없음).
 *
 * 0건이면 섹션 전체를 숨긴다(빈 표/EmptyState 미노출 — 노이즈 억제). 속성이 많은 artifact
 * (예: eraf-core)는 접두(prefix) 기준 그룹 헤더로 접이식 렌더하고, 초기엔 최상위(가장 짧은)
 * 접두 그룹만 펼친다 — 대량 표 스크롤 완화(설계 결정).
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyButton, StatusPill } from '@ui-kit';
import type { ModuleNode, ConfigPropertyEntry } from '../types/showcase';
import { useConfigProperties } from '../hooks/useConfigProperties';

/** 이 개수 초과 시에만 접두 그룹 접이식 렌더 — 이하는 평면 표가 더 읽기 쉽다(설계 결정). */
const COLLAPSIBLE_THRESHOLD = 15;

export interface ConfigPropertyGroup {
  /** 속성명에서 마지막 세그먼트를 제외한 상위 접두 (예: "eraf.common.actuator") */
  prefix: string;
  properties: ConfigPropertyEntry[];
}

/**
 * 속성명 상위 접두 기준 그룹핑(순수 함수 — 단위테스트 대상). 짧은(더 상위) 접두 그룹이
 * 앞에 오도록 정렬한다 — "초기엔 상위 접두만 펼침"(§R665-3) 이 첫 그룹을 가리키게 하기 위함.
 */
export function groupConfigProperties(properties: ConfigPropertyEntry[]): ConfigPropertyGroup[] {
  const map = new Map<string, ConfigPropertyEntry[]>();
  for (const p of properties) {
    const segments = p.name.split('.');
    const prefix = segments.length > 1 ? segments.slice(0, -1).join('.') : p.name;
    if (!map.has(prefix)) map.set(prefix, []);
    map.get(prefix)!.push(p);
  }
  return Array.from(map.entries())
    .map(([prefix, props]) => ({ prefix, properties: props }))
    .sort((a, b) => a.prefix.split('.').length - b.prefix.split('.').length || a.prefix.localeCompare(b.prefix));
}

// ─── styles ────────────────────────────────────────────────────────────────

const sectionTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-lg)',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  margin: '0 0 var(--spacing-1)',
};

const captionStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  margin: '0 0 var(--spacing-3)',
};

const codeStyle: CSSProperties = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-brand-primary)',
};

const headerCellStyle: CSSProperties = {
  textAlign: 'left',
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '1px solid var(--color-surface-border)',
  color: 'var(--color-text-muted)',
  fontWeight: 600,
  fontSize: 'var(--font-size-xs)',
};

const cellStyle: CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '1px solid var(--color-surface-border)',
  verticalAlign: 'top',
  color: 'var(--color-text-secondary)',
};

const groupBoxStyle: CSSProperties = {
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  overflow: 'hidden',
};

const groupHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  padding: 'var(--spacing-2) var(--spacing-3)',
  background: 'var(--color-surface-muted)',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const envBoxStyle: CSSProperties = {
  marginTop: 'var(--spacing-3)',
  padding: 'var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-elevated)',
};

// ─── row / table renderers ────────────────────────────────────────────────

function ConfigPropertyRow({ property }: { property: ConfigPropertyEntry }) {
  const { t } = useTranslation();
  const defaultDisplay =
    property.defaultValue === null || property.defaultValue === undefined || property.defaultValue === ''
      ? '—'
      : String(property.defaultValue);

  return (
    <tr id={`config-${property.name}`}>
      <td style={cellStyle}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
          <code style={codeStyle}>{property.name}</code>
          <CopyButton
            text={property.name}
            ariaLabel={t('showcase.takeThis.copyAria', { block: property.name })}
            copyLabel={t('showcase.takeThis.copy')}
            copiedLabel={t('showcase.takeThis.copied')}
          />
        </span>
      </td>
      <td style={cellStyle}>
        <code style={codeStyle}>{property.type}</code>
      </td>
      <td style={cellStyle}>{defaultDisplay}</td>
      <td style={cellStyle}>{property.description}</td>
      <td style={cellStyle}>
        {property.sensitive && (
          <span title={t('showcase.config.section.sensitive.tip')}>
            <StatusPill tone="warning">{t('showcase.config.section.sensitive.badge')}</StatusPill>
          </span>
        )}
      </td>
    </tr>
  );
}

function ConfigPropertyTable({ properties }: { properties: ConfigPropertyEntry[] }) {
  const { t } = useTranslation();
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
      <thead>
        <tr>
          <th style={headerCellStyle}>{t('showcase.config.section.col.name')}</th>
          <th style={headerCellStyle}>{t('showcase.config.section.col.type')}</th>
          <th style={headerCellStyle}>{t('showcase.config.section.col.default')}</th>
          <th style={headerCellStyle}>{t('showcase.config.section.col.desc')}</th>
          <th style={headerCellStyle}>{t('showcase.config.section.col.sensitive')}</th>
        </tr>
      </thead>
      <tbody>
        {properties.map((p) => (
          <ConfigPropertyRow key={p.name} property={p} />
        ))}
      </tbody>
    </table>
  );
}

function ConfigEnvCanonicalNotice() {
  const { t } = useTranslation();
  return (
    <div style={envBoxStyle}>
      <p style={{ margin: 0, fontWeight: 600, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
        {t('showcase.config.env.title')}
      </p>
      <p style={{ margin: 'var(--spacing-1) 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
        {t('showcase.config.env.rule')}
      </p>
      <code style={{ ...codeStyle, display: 'inline-block', marginTop: 'var(--spacing-1)' }}>
        {t('showcase.config.env.example')}
      </code>
    </div>
  );
}

// ─── main section ──────────────────────────────────────────────────────────

interface Props {
  node: ModuleNode;
}

export function ConfigSection({ node }: Props) {
  const { t } = useTranslation();
  const properties = useConfigProperties(node.artifact);
  const shouldGroup = properties.length > COLLAPSIBLE_THRESHOLD;
  const groups = useMemo(() => (shouldGroup ? groupConfigProperties(properties) : null), [shouldGroup, properties]);
  const [expandedPrefixes, setExpandedPrefixes] = useState<Set<string>>(new Set());

  useEffect(() => {
    // 초기엔 최상위(가장 짧은) 접두 그룹만 펼친다(§R665-3) — 대량 표 스크롤 완화.
    setExpandedPrefixes(groups && groups.length > 0 ? new Set([groups[0].prefix]) : new Set());
  }, [groups]);

  // §R665-3 empty/error — 0건(또는 잘못된 shape 파싱 방어로 빈 배열)이면 섹션 전체 숨김.
  if (properties.length === 0) return null;

  function togglePrefix(prefix: string) {
    setExpandedPrefixes((prev) => {
      const next = new Set(prev);
      if (next.has(prefix)) next.delete(prefix);
      else next.add(prefix);
      return next;
    });
  }

  return (
    <section aria-labelledby="config-section-title" style={{ marginBottom: 'var(--spacing-4)' }}>
      <h3 id="config-section-title" style={sectionTitleStyle}>
        {t('showcase.config.section.title')}
      </h3>
      <p style={captionStyle}>{t('showcase.config.section.caption', { artifact: node.artifact })}</p>

      {groups ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
          {groups.map((group) => {
            const isExpanded = expandedPrefixes.has(group.prefix);
            return (
              <div key={group.prefix} style={groupBoxStyle}>
                <button
                  type="button"
                  onClick={() => togglePrefix(group.prefix)}
                  aria-expanded={isExpanded}
                  style={groupHeaderStyle}
                >
                  <code style={codeStyle}>{group.prefix}</code>
                  <span aria-hidden="true">{isExpanded ? '▾' : '▸'}</span>
                </button>
                {isExpanded && <ConfigPropertyTable properties={group.properties} />}
              </div>
            );
          })}
        </div>
      ) : (
        <ConfigPropertyTable properties={properties} />
      )}

      <ConfigEnvCanonicalNotice />
    </section>
  );
}
